import React from "react";
import { Users, LineChart, Dumbbell, Utensils, Calendar, MessageCircle } from "lucide-react";

const FEATURES = [
  {
    icon: Users,
    title: "Client Management",
    description: "Onboard clients in seconds. Goals, measurements, photos and payments — one organised profile per client.",
    points: ["Unlimited client profiles", "Before / after photo logs", "Payment tracking"],
  },
  {
    icon: LineChart,
    title: "Progress Tracking",
    description: "Weight, body composition and performance in beautiful charts clients can't wait to check every week.",
    points: ["Auto-generated progress charts", "Custom KPIs & milestones", "Weekly progress reports"],
  },
  {
    icon: Dumbbell,
    title: "Workout Builder",
    description: "Build, reuse and assign workouts in minutes. Clients follow video-rich exercises straight from their phone.",
    points: ["500+ exercise library", "Reusable templates", "Phone-friendly player"],
  },
  {
    icon: Utensils,
    title: "Diet Planner",
    description: "Macro-aware meal plans with Indian foods. Your clients finally stick to what you prescribe.",
    points: ["Macro & calorie calculator", "Indian food database", "Shopping list export"],
  },
  {
    icon: Calendar,
    title: "Scheduling & Reminders",
    description: "Sessions booked, reminders sent. Fewer no-shows, more results, more retention.",
    points: ["Session calendar", "Auto WhatsApp / email reminders", "Reschedule in one tap"],
  },
  {
    icon: MessageCircle,
    title: "Client Communication",
    description: "Share updates, answer questions and celebrate wins — without digging through endless WhatsApp chats.",
    points: ["In-app messaging", "Share plans & files", "Check-in forms"],
  },
];

export default function Features() {
  return (
    <section id="features" className="py-20 md:py-28 relative" data-testid="features-section">
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(34,211,238,0.06), transparent 50%)" }}
      />
      <div className="section-container relative">
        <div className="text-center max-w-2xl mx-auto mb-14 reveal">
          <div className="chip mb-4">Features</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">
            Everything you need to <span className="gradient-text">run your coaching business</span>
          </h2>
          <p className="mt-4 text-white/60">
            From the first client check-in to the last rep tracked — FitTrackCoach handles it, end to end.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                className="card-surface rounded-2xl p-6 md:p-7 reveal"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                     style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.2), rgba(59,130,246,0.15))", border: "1px solid rgba(34,211,238,0.3)" }}>
                  <Icon size={20} className="text-cyan-300" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-4">{f.description}</p>
                <ul className="space-y-1.5">
                  {f.points.map((p, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-white/55">
                      <span className="w-1 h-1 rounded-full bg-cyan-400" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
